import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Filter } from "lucide-react";

function priorityChip(p) {
  const map = {
    low:      "bg-slate-100 text-slate-700 border-slate-200",
    medium:   "bg-sky-100 text-sky-800 border-sky-200",
    high:     "bg-amber-100 text-amber-900 border-amber-300",
    critical: "bg-red-100 text-red-800 border-red-200",
  };
  const cls = map[(p || "medium").toLowerCase()] || map.medium;
  return <span className={`inline-flex items-center text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-full border ${cls}`}>{p || "medium"}</span>;
}

function statusChip(s) {
  const map = {
    open:        "bg-slate-100 text-slate-700 border-slate-200",
    in_progress: "bg-sky-100 text-sky-800 border-sky-200",
    completed:   "bg-emerald-100 text-emerald-800 border-emerald-200",
    overdue:     "bg-red-100 text-red-800 border-red-200",
  };
  const cls = map[s] || map.open;
  return <span className={`inline-flex items-center text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-full border ${cls}`}>{(s || "open").replace(/_/g, " ")}</span>;
}

export default function AuditFindingsTab({ onOpenAudit, refreshTick }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/audits-qa/findings/all", { params: { ...(status ? { status } : {}), ...(priority ? { priority } : {}) } });
      setRows(r.data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [status, priority]);

  useEffect(() => { load(); }, [load, refreshTick]);

  return (
    <section className="bg-white border border-slate-200 rounded-lg" data-testid="audits-qa-findings-tab">
      <div className="p-4 border-b border-slate-200 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-base font-semibold text-slate-900">Findings &amp; Improvement Actions</h2>
          <p className="text-xs text-slate-500 mt-0.5">Flat view of every corrective action across audits.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="findings-filter-status" className="h-8 text-xs border border-slate-300 rounded-md bg-white px-2 capitalize">
            <option value="">All statuses</option>
            <option value="open">Open</option>
            <option value="in_progress">In progress</option>
            <option value="completed">Completed</option>
            <option value="overdue">Overdue</option>
          </select>
          <select value={priority} onChange={(e) => setPriority(e.target.value)} data-testid="findings-filter-priority" className="h-8 text-xs border border-slate-300 rounded-md bg-white px-2 capitalize">
            <option value="">All priorities</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="critical">Critical</option>
          </select>
        </div>
      </div>
      {loading ? <div className="p-8 text-sm text-slate-500">Loading…</div>
        : rows.length === 0 ? <div className="p-16 text-center text-sm text-slate-500">No findings match — nice work.</div>
        : (
        <div className="overflow-x-auto">
          <table className="data-table w-full" data-testid="findings-table">
            <thead><tr>
              <th>Finding</th><th>Corrective Action</th><th>Assigned</th>
              <th>Due</th><th>Priority</th><th>Status</th><th className="w-16 text-right">Open</th>
            </tr></thead>
            <tbody>
              {rows.map((f) => (
                <tr key={f.id} data-testid={`finding-row-${f.id}`}>
                  <td>{f.finding}</td>
                  <td className="text-slate-600">{f.corrective_action || "—"}</td>
                  <td className="text-slate-700">{f.assigned_to_name || "—"}</td>
                  <td className="tabular-nums">{f.due_date || "—"}</td>
                  <td>{priorityChip(f.priority)}</td>
                  <td>{statusChip(f.status)}</td>
                  <td className="text-right">
                    <button onClick={() => onOpenAudit?.(f.audit_id)} className="text-xs text-sky-700 hover:underline" data-testid={`btn-open-finding-audit-${f.id}`}>Open</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
